/**
 * The cost ledger — ENGINEERING §6.
 *
 * Every dispatch is counted, **including failures**, so the total is an upper bound rather
 * than an invoice. That is the useful direction to be wrong in: the number exists to stop a
 * runaway, and a runaway is mostly failures.
 *
 * This is the in-process guard. It is not the hard cap — that is a prepaid fal balance with
 * auto-recharge off, plus a GCP billing alarm (§10, deferred #3). At $0.08 an image with five
 * in flight, an unattended loop burns roughly $100 an hour.
 */
import { SpendCapError } from './errors.js';
/*
 * Reachable as `@atlas-nook/image-worker/cost` as well as through the package index.
 *
 * The Studio has to *state the price before it spends* (PRD 04 §4.3), so it needs `priceOf`
 * in a browser bundle — and the package index pulls `sharp` and the fal client, neither of
 * which can be bundled for one. This module imports nothing but a type and an error class,
 * so the subpath is the whole seam. Nothing else about the worker crosses into a browser.
 */
/** Nano Banana 2 on fal, at 1K, recorded 2026-08-15. */
export const NANO_BANANA_BASE_USD = 0.08;
/** Priced by the resolution actually sent. A 2K model sheet is ~$0.12; 4K doubles it. */
export const RESOLUTION_MULTIPLIER = Object.freeze({
    '0.5K': 0.75,
    '1K': 1,
    '2K': 1.5,
    '4K': 2,
});
export function priceOf(resolution = '1K') {
    return NANO_BANANA_BASE_USD * RESOLUTION_MULTIPLIER[resolution];
}
export class CostLedger {
    capUsd;
    entries = [];
    now;
    constructor(options = {}) {
        this.capUsd = options.capUsd === undefined ? null : options.capUsd;
        this.now = options.now ?? Date.now;
    }
    /** Count a dispatch. Failures count too — they were paid for either way. */
    record(entry) {
        const recorded = { ...entry, at: entry.at ?? this.now() };
        this.entries.push(recorded);
        return recorded;
    }
    get totalUsd() {
        return this.entries.reduce((sum, entry) => sum + entry.usd, 0);
    }
    get count() {
        return this.entries.length;
    }
    all() {
        return [...this.entries];
    }
    /** Throw before dispatching something that would cross the cap. Uncapped never throws. */
    assertCapacity(nextUsd) {
        if (this.capUsd === null)
            return;
        if (this.totalUsd + nextUsd > this.capUsd) {
            throw new SpendCapError(this.totalUsd, nextUsd, this.capUsd);
        }
    }
}
//# sourceMappingURL=cost.js.map